import { Link, Navigate, useParams } from 'react-router-dom'
import Seo from '@/components/Seo'
import SiteLayout from '@/components/SiteLayout'
import PageHero from '@/components/PageHero'
import FeatureSection from '@/components/FeatureSection'
import CtaBand from '@/components/CtaBand'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { getGuide, getRelatedGuides } from '@/content/guides'
import { cardImageOf, heroImageOf, sectionsWithImages } from '@/content/applyImages'
import { SITE_URL } from '@/components/Seo'

export default function GuideDetailPage() {
  const { slug } = useParams()
  const guide = getGuide(slug)

  if (!guide) {
    return <Navigate to="/guides" replace />
  }

  const related = getRelatedGuides(guide.related)
  const heroSrc = heroImageOf(guide)
  const steps = sectionsWithImages({ images: guide.images, sections: guide.steps })

  return (
    <SiteLayout>
      <Seo
        title={guide.seoTitle}
        description={guide.seoDescription}
        path={guide.path}
        image={heroSrc}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: guide.title,
          description: guide.seoDescription,
          url: `${SITE_URL}${guide.path}`,
          step: guide.steps.map((step, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: step.title,
            text: step.description,
          })),
        }}
      />
      <PageHero
        title={guide.title}
        description={guide.summary}
        image={heroSrc}
        eyebrow="Guide"
      />

      {steps.map((step, index) => (
        <FeatureSection
          key={step.title}
          title={`${index + 1}. ${step.title}`}
          description={step.description}
          image={step.image}
          reverse={index % 2 === 1}
          className={index % 2 === 0 ? 'bg-background' : 'bg-slate-50'}
        />
      ))}

      {guide.faqs?.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto max-w-3xl px-6">
            <h2 className="text-3xl font-bold mb-8 text-center">Common questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {guide.faqs.map((faq, index) => (
                <AccordionItem key={faq.q} value={`faq-${index}`}>
                  <AccordionTrigger className="text-left">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="py-16 bg-background">
          <div className="container mx-auto max-w-7xl px-6">
            <h2 className="text-3xl font-bold text-center mb-10">Related guides</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((item) => (
                <Link key={item.slug} to={item.path} className="block group">
                  <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="h-36 overflow-hidden">
                      <img
                        src={cardImageOf(item)}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <CardHeader>
                      <CardTitle className="text-lg">{item.title}</CardTitle>
                      <CardDescription className="line-clamp-2">{item.summary}</CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBand
        title="Ready to connect Stripe?"
        description="Get started with Tillio and authorize your existing Stripe account for Terminal payments."
      />
    </SiteLayout>
  )
}
